import { Component } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import { StarRounded, StarBorderRounded } from "@mui/icons-material"
import { hasDefinedValue } from "components/charts/helpers/has-defined-value"
import Tooltip from "components/material/tooltip"

class LegendItem extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    // Absent for a series not currently plotted, which renders a neutral dot instead of a swatch.
    color: PropTypes.string,
    onlyLegendText: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    focus: PropTypes.func.isRequired,
    blur: PropTypes.func.isRequired,
    onOnlyToggle: PropTypes.func.isRequired,
    focused: PropTypes.string,
    favorite: PropTypes.bool,
    // Right-hand legend panel only — the inline legends never pass this.
    showValue: PropTypes.bool,
    value: PropTypes.number,
    formatter: PropTypes.func,
    // Omitted (not just guarded) for a disabled row — a hidden series must not be drillable.
    onValueClick: PropTypes.func,
    // YYYY-MM-DD of `value`; shown in the value tooltip.
    valueDate: PropTypes.string,
    // "end" moves the dot after the Only/All link (right-hand legend panel).
    dotPlacement: PropTypes.oneOf(["start", "end"]),
  }

  onClick = () => {
    const { id, disabled, onClick } = this.props
    onClick(id, disabled)
  }

  onValueClick = () => {
    const { id, disabled, onValueClick } = this.props
    if (disabled) return
    onValueClick(id)
  }

  onValueKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    this.onValueClick()
  }

  onEnter = () => {
    const { id, focus, disabled } = this.props
    !disabled && focus(id)
  }

  onLeave = (e) => {
    const { blur, disabled } = this.props
    if (disabled) return
    // Only blur when focus/pointer truly leaves the row; relatedTarget may be `window`, not a Node, at the document edge.
    const relatedTarget = e?.relatedTarget
    if (relatedTarget instanceof Node && e.currentTarget.contains(relatedTarget)) return
    blur()
  }

  toggleOnly = () => {
    const { id, onOnlyToggle, onlyLegendText } = this.props
    onOnlyToggle(id, onlyLegendText)
  }

  render() {
    const { color, label, disabled, focused, id, favorite, onlyLegendText, value, showValue, formatter, onValueClick, valueDate, dotPlacement = "start" } = this.props
    const formattedValue = hasDefinedValue(value)
      ? (formatter ? formatter(value) : value)
      : "—"
    const disabledModifier = color ? "disabled" : "unplotted"
    let icon

    if (favorite) {
      icon = disabled
        ? <StarBorderRounded className="star-icon" style={{ color }} />
        : <StarRounded className="star-icon" style={{ color }} />
    } else if (!color) {
      icon = <span className="color color--unplotted" />
    } else {
      icon = (
        <span
          className={classNames("color", disabled && "color--disabled")}
          style={{
            borderColor: color,
            ...(!disabled && { backgroundColor: color }),
          }}
        />
      )
    }

    const isDotAtEnd = dotPlacement === "end"
    const isValueClickable = Boolean(onValueClick) && !disabled
    const valueTooltip = hasDefinedValue(value) && !disabled
      ? [
        <strong key="value">{formattedValue}</strong>,
        valueDate ? `${label} · ${valueDate}` : label,
        isValueClickable && "Click to view contributors",
      ].filter(Boolean)
      : null

    return <div
      onMouseEnter={this.onEnter}
      onMouseLeave={this.onLeave}
      onFocus={this.onEnter}
      onBlur={this.onLeave}
      className={classNames(
        // Stable hook for container-specific layout CSS (e.g. the right-hand legend panel).
        "legend-item",
        "focus-enabled",
        focused && focused === id && "visible",
        focused && focused !== id && "hidden",
      )}
    >
      <button
        type="button"
        onClick={this.onClick}
        className="label-wrapper"
        aria-label={`${disabled ? "Show" : "Hide"} ${label}`}
      >
        {!isDotAtEnd && icon}
        <Tooltip title={label}>
          <span className={classNames("label", disabled && `label--${disabledModifier}`)}>{label}</span>
        </Tooltip>
      </button>
      {showValue && (
        <Tooltip title={valueTooltip || ""}>
          <div
            onClick={isValueClickable ? this.onValueClick : undefined}
            onKeyDown={isValueClickable ? this.onValueKeyDown : undefined}
            role={isValueClickable ? "button" : undefined}
            tabIndex={isValueClickable ? 0 : undefined}
            className={classNames(
              "value",
              disabled && `value--${disabledModifier}`,
              isValueClickable && "value--clickable",
            )}
          >
            {formattedValue}
          </div>
        </Tooltip>
      )}
      <button
        type="button"
        onClick={this.toggleOnly}
        style={{ color }}
        className={classNames(
          "only-link",
          focused && focused === id && "visible",
          focused && focused !== id && "hidden",
        )}
      >
        {onlyLegendText}
      </button>
      {isDotAtEnd && (
        // Mouse affordance only: the label button above is the accessible toggle.
        <button type="button" onClick={this.onClick} className="dot-toggle" tabIndex={-1} aria-hidden="true">
          {icon}
        </button>
      )}
    </div>
  }
}

export default LegendItem
